/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { JournalVoucher9Info } from "./info";
import { JournalVoucher9Service } from "services/journal-voucher-9";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const emptyGroupData = () => ({
  values: [0, 0, 0, 0],
  total: 0,
});

const emptyRow = () => ({
  index: 0,
  docNumber: "",
  docDate: "",
  description: "",
  group211: emptyGroupData(),
  group212: emptyGroupData(),
  group213: emptyGroupData(),
  group215: emptyGroupData(),
  group217: emptyGroupData(),
});

const initialValues = {
  unit: "",
  address: "",
  month: (today.getMonth() + 1).toString(),
  year: today.getFullYear().toString(),
  groupLabels: {
    labels211: ["214", "811", "222", ""],
    labels212: ["211", "213", "214", ""],
    labels213: ["214", "811", "", ""],
    labels215: ["152", "154", "632", ""],
    labels217: ["211", "213", "214", ""],
  },
  rows: [emptyRow()],
  totals: {
    group211: emptyGroupData(),
    group212: emptyGroupData(),
    group213: emptyGroupData(),
    group215: emptyGroupData(),
    group217: emptyGroupData(),
  },
  recordedToLedgerDay: "",
  recordedToLedgerMonth: "",
  recordedToLedgerYear: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function JournalVoucher9Detail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await JournalVoucher9Service.getById(id);
          const apiData = response.data || response;

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...apiData,
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/journal-voucher-9");

  const num = (v) => (v !== undefined && v !== "" && !isNaN(v) ? Number(v) : 0);

  const mapGroupData = (g) => ({
    values: (g.values || [0, 0, 0, 0]).map(num),
    total: num(g.total),
  });

  const submitForm = async (fValues) => {
    try {
      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        month: fValues.month,
        year: fValues.year,
        groupLabels: fValues.groupLabels,
        rows: (fValues.rows || []).map((r, idx) => ({
          ...r,
          index: r.index || idx + 1,
          group211: mapGroupData(r.group211),
          group212: mapGroupData(r.group212),
          group213: mapGroupData(r.group213),
          group215: mapGroupData(r.group215),
          group217: mapGroupData(r.group217),
        })),
        totals: {
          group211: mapGroupData(fValues.totals.group211),
          group212: mapGroupData(fValues.totals.group212),
          group213: mapGroupData(fValues.totals.group213),
          group215: mapGroupData(fValues.totals.group215),
          group217: mapGroupData(fValues.totals.group217),
        },
        recordedToLedgerDay: fValues.recordedToLedgerDay,
        recordedToLedgerMonth: fValues.recordedToLedgerMonth,
        recordedToLedgerYear: fValues.recordedToLedgerYear,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await JournalVoucher9Service.create(payload);
        toast.success("Tạo nhật ký chứng từ thành công!");
      } else {
        await JournalVoucher9Service.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/journal-voucher-9");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S04a9-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <JournalVoucher9Info formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Nhật ký S04a9-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucher9Detail;
